import { useEffect, useState } from "react";
import AccountButton from "./AccountButton";
import AccountInput from "./AccountInput";
import AccountSection from "./AccountSection";
import {
  addLifestyleInfo,
  getLifestyleInfo,
  updateLifestyleInfo,
} from "./api/apiFunctions";
import { t } from "./i18n";
import { getAuthToken } from "./utils/auth";
import { useSubscriptionAccess } from "./context/SubscriptionAccessContext";

export default function LifestylePage({ lang = "en", goToTab }) {
  const { dontHaveAccess } = useSubscriptionAccess();
  const [form, setForm] = useState({
    religion: "",
    customReligion: "",
    worshipPlace: "",
    worshipName: "",
    customWorshipPlace: "",
    worshipAddress: "",
    worshipPlaceName: "",
    maritalStatus: "",
    workStatus: "",
    education: "",
    occupation: "",
    dietaryRoutine: "",
    exerciseFrequency: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [lifestyleId, setLifestyleId] = useState(null);

  // Dropdown options using current lang - matching API schema with proper capitalization
  const maritalStatusOptions = [
    { value: "", label: t("choose", lang) },
    { value: "single", label: t("single", lang) },
    { value: "married", label: t("married", lang) },
    { value: "divorced", label: t("divorced", lang) },
    { value: "widowed", label: t("widowed", lang) },
    { value: "separated", label: t("separated", lang) },
    { value: "other", label: t("other", lang) },
  ];
  const workStatusOptions = [
    { value: "", label: t("choose", lang) },
    { value: "employed", label: t("employed", lang) },
    { value: "unemployed", label: t("unemployed", lang) },
    { value: "retired", label: t("retired", lang) },
    { value: "student", label: t("student", lang) },
    { value: "self-employed", label: t("selfEmployed", lang) },
    { value: "other", label: t("other", lang) },
  ];
  const educationOptions = [
    { value: "", label: t("choose", lang) },
    { value: "no formal education", label: t("noFormalEducation", lang) },
    { value: "primary school", label: t("primarySchool", lang) },
    { value: "secondary school", label: t("secondarySchool", lang) },
    { value: "high school", label: t("highSchool", lang) },
    { value: "associate degree", label: t("associateDegree", lang) },
    { value: "bachelor's degree", label: t("bachelorDegree", lang) },
    { value: "master's degree", label: t("masterDegree", lang) },
    { value: "doctorate", label: t("doctorate", lang) },
    { value: "other", label: t("other", lang) },
  ];
  const occupationOptions = [
    { value: "", label: t("choose", lang) },
    { value: "engineer", label: t("engineer", lang) },
    { value: "teacher", label: t("teacher", lang) },
    { value: "doctor", label: t("doctor", lang) },
    { value: "nurse", label: t("nurse", lang) },
    { value: "lawyer", label: t("lawyer", lang) },
    { value: "business", label: t("business", lang) },
    { value: "student", label: t("student", lang) },
    { value: "retired", label: t("retired", lang) },
    { value: "other", label: t("other", lang) },
  ];
  const dietaryRoutineOptions = [
    { value: "", label: t("choose", lang) },
    { value: "omnivore", label: t("omnivore", lang) },
    { value: "vegetarian", label: t("vegetarian", lang) },
    { value: "vegan", label: t("vegan", lang) },
    { value: "pescatarian", label: t("pescatarian", lang) },
    { value: "keto", label: t("keto", lang) },
    { value: "paleo", label: t("paleo", lang) },
    { value: "mediterranean", label: t("mediterranean", lang) },
    { value: "gluten-free", label: t("glutenFree", lang) },
    { value: "dairy-free", label: t("dairyFree", lang) },
    { value: "other", label: t("other", lang) },
  ];
  const exerciseFrequencyOptions = [
    { value: "", label: t("choose", lang) },
    { value: "never", label: t("never", lang) },
    { value: "rarely", label: t("rarely", lang) },
    { value: "1-2 times per week", label: t("oneToTwoTimesPerWeek", lang) },
    { value: "3-4 times per week", label: t("threeToFourTimesPerWeek", lang) },
    { value: "5+ times per week", label: t("fivePlusTimesPerWeek", lang) },
    { value: "daily", label: t("daily", lang) },
    { value: "other", label: t("other", lang) },
  ];
  const religionOptions = [
    { value: "", label: t("choose", lang) },
    { value: "catholic", label: t("catholic", lang) },
    { value: "protestant", label: t("protestant", lang) },
    { value: "orthodox", label: t("orthodox", lang) },
    { value: "baptist", label: t("baptist", lang) },
    { value: "methodist", label: t("methodist", lang) },
    { value: "lutheran", label: t("lutheran", lang) },
    { value: "presbyterian", label: t("presbyterian", lang) },
    { value: "episcopal", label: t("episcopal", lang) },
    { value: "christianity", label: t("christianity", lang) },
    { value: "islam", label: t("islam", lang) },
    { value: "hinduism", label: t("hinduism", lang) },
    { value: "buddhism", label: t("buddhism", lang) },
    { value: "judaism", label: t("judaism", lang) },
    { value: "sikhism", label: t("sikhism", lang) },
    { value: "other", label: t("other", lang) },
  ];
  const worshipPlaceOptions = [
    { value: "", label: t("choose", lang) },
    { value: "church", label: t("church", lang) },
    { value: "mosque", label: t("mosque", lang) },
    { value: "temple", label: t("temple", lang) },
    { value: "synagogue", label: t("synagogue", lang) },
    { value: "gurdwara", label: t("gurdwara", lang) },
    { value: "shrine", label: t("shrine", lang) },
    { value: "chapel", label: t("chapel", lang) },
    { value: "cathedral", label: t("cathedral", lang) },
    { value: "monastery", label: t("monastery", lang) },
    { value: "home", label: t("home", lang) },
    { value: "communityCenter", label: t("communityCenter", lang) },
    { value: "other", label: t("other", lang) },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    loadLifestyleData();
  }, []);

  const loadLifestyleData = async () => {
    try {
      setLoading(true);
      setError("");
      const token = getAuthToken();

      if (!token) {
        setError("Please log in to view your lifestyle information");
        return;
      }

      const response = await getLifestyleInfo(token);

      // Handle both response formats: {success: true, data: {...}} or direct data object
      const data = response.success ? response.data : response;

      if (data) {
        setForm({
          religion: data.religion,
          customReligion: "",
          worshipPlace: data.worshipPlace || "",
          worshipName: data.worshipName || "",
          customWorshipPlace: "",
          worshipAddress: data.worshipAddress || "",
          worshipPlaceName: data.worshipPlaceName || "",
          maritalStatus: data.maritalStatus || "",
          workStatus: data.workStatus || "",
          education: data.education || "",
          occupation: data.occupation || "",
          dietaryRoutine: data.dietaryRoutine || "",
          exerciseFrequency: data.exerciseFrequency || "",
        });
        // Try different possible ID field names
        const fetchedId = data.lifestyleId || data._id || data.id;
        if (fetchedId) {
          setLifestyleId(fetchedId);
        }
      }
    } catch (error) {
      console.error("Error loading lifestyle data:", error);
      // Don't set error for 404 - just means no data exists yet
      if (
        error.message.includes("404") ||
        error.message.includes("not found")
      ) {
      } else {
        setError("Failed to load lifestyle information. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) {
        setError("Please log in to save your lifestyle information");
        return;
      }

      // Create a working copy of the form to auto-fill empty required enum fields
      const workingForm = { ...form };

      // Prepare data for API - map form fields to API schema using workingForm
      const lifestyleData = {
        religion:
          workingForm.religion === "other"
            ? workingForm.customReligion
            : workingForm.religion,
        worshipPlace:
          workingForm.worshipPlace === "other"
            ? workingForm.customWorshipPlace
            : workingForm.worshipPlace,
        worshipName: workingForm.worshipName || "",
        worshipAddress: workingForm.worshipAddress || "",
        worshipPlaceName: workingForm.worshipPlaceName || "",
        maritalStatus: workingForm.maritalStatus,
        workStatus: workingForm.workStatus,
        education: workingForm.education,
        occupation: workingForm.occupation || "",
        dietaryRoutine: workingForm.dietaryRoutine,
        exerciseFrequency: workingForm.exerciseFrequency,
      };

      let response;
      try {
        if (lifestyleId) {
          console.log("life style id", lifestyleId);
          // Update existing lifestyle
          response = await updateLifestyleInfo(lifestyleData, token);
        } else {
          // Create new lifestyle
          response = await addLifestyleInfo(lifestyleData, token);
        }

        if (response.success) {
          setSuccess("Lifestyle information saved successfully!");
          if (response.data && response.data.lifestyleId) {
            setLifestyleId(response.data.lifestyleId);
          }
          // Clear success message after 3 seconds
          setTimeout(() => setSuccess(""), 3000);
          return true; // Return true to indicate success
        }
        return false; // Return false if response was not successful
      } catch (createError) {
        // Check if error is "already exists" - if so, fetch existing data and retry with update
        const errorMessage = createError.message || "";
        const lowerErrorMessage = errorMessage.toLowerCase();

        // Check for various forms of "already exists" error messages
        if (
          lowerErrorMessage.includes("already exists") ||
          lowerErrorMessage.includes("use update") ||
          lowerErrorMessage.includes("lifestyle information already exists")
        ) {
          try {
            // Fetch existing lifestyle info to get the ID
            const existingData = await getLifestyleInfo(token);

            // Handle both response formats: {success: true, data: {...}} or direct data object
            const data = existingData?.success
              ? existingData.data
              : existingData;

            if (data) {
              // Set the lifestyle ID from the fetched data
              const fetchedId = data.lifestyleId || data._id || data.id;
              if (fetchedId) {
                setLifestyleId(fetchedId);
                // Clear error before retrying
                setError("");
                // Retry with update endpoint
                response = await updateLifestyleInfo(lifestyleData, token);
                // Handle both response formats
                const updateSuccess = response?.success !== false; // Consider it successful if not explicitly false
                if (updateSuccess) {
                  setSuccess("Lifestyle information saved successfully!");
                  // Clear success message after 3 seconds
                  setTimeout(() => setSuccess(""), 3000);
                  return true;
                } else {
                  throw new Error(
                    response?.message ||
                      "Failed to update lifestyle information",
                  );
                }
              } else {
                console.error("Could not find lifestyle ID in response:", data);
                throw createError;
              }
            } else {
              console.error(
                "Could not fetch existing lifestyle data:",
                existingData,
              );
              throw createError;
            }
          } catch (fetchError) {
            console.error(
              "Error fetching existing lifestyle data:",
              fetchError,
            );
            // If fetching existing data fails, throw the original error
            // throw createError;
          }
        } else {
          // For other errors, throw them
          // throw createError;
        }
      }
    } catch (error) {
      console.error("Error saving lifestyle data:", error);
      setError(
        error.message ||
          "Failed to save lifestyle information. Please try again.",
      );
      return false; // Return false to indicate failure
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center p-4 md:p-8">
      <div className="w-full max-w-4xl">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          <AccountSection>
            {t("lifestyleInfo", lang) || "Lifestyle Information"}
          </AccountSection>

          {/* Error and Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded">
              Loading lifestyle information...
            </div>
          )}
          {/* Religion as a single line dropdown */}
          <div className="mb-6">
            <label className="text-xs font-semibold text-[#837f90] mb-1 block">
              {t("religion", lang) || "Religion"}
            </label>
            <select
              name="religion"
              value={form.religion}
              onChange={handleChange}
              className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
            >
              {religionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Custom religion input when "other" is selected */}
            {form.religion === "other" && (
              <div className="mt-4">
                <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                  {t("pleaseSpecify", lang) || "Please specify"}
                </label>
                <input
                  type="text"
                  name="customReligion"
                  value={form.customReligion}
                  onChange={handleChange}
                  placeholder={
                    t("enterCustomReligion", lang) || "Enter your religion"
                  }
                  className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                />
              </div>
            )}
          </div>
          {/* Place of Worship and Address side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("placeOfWorship", lang) || "Place of Worship"}
              </label>
              <select
                name="worshipPlace"
                value={form.worshipPlace}
                onChange={handleChange}
                disabled={dontHaveAccess}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {worshipPlaceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {/* Custom worship place input when "other" is selected */}
              {form.worshipPlace === "other" && (
                <div className="mt-4">
                  <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                    {t("pleaseSpecify", lang) || "Please specify"}
                  </label>
                  <input
                    type="text"
                    name="customWorshipPlace"
                    value={form.customWorshipPlace}
                    disabled={dontHaveAccess}
                    onChange={handleChange}
                    placeholder={
                      t("enterPlaceOfWorship", lang) || "Enter place of worship"
                    }
                    className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                  />
                </div>
              )}
            </div>
            <AccountInput
              label={
                t("placeOfWorshipAddress", lang) || "Place of Worship Address"
              }
              name="worshipAddress"
              value={form.worshipAddress}
              onChange={handleChange}
              disabled={dontHaveAccess}
            />
            <AccountInput
              label={"Place of Worship Name"}
              name="worshipPlaceName"
              value={form.worshipPlaceName}
              onChange={handleChange}
              disabled={dontHaveAccess}
            />
          </div>
          <AccountSection>
            {t("workAndHome", lang) || "Work and Home"}
          </AccountSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("maritalStatus", lang) || "Marital Status"}
              </label>
              <select
                name="maritalStatus"
                value={form.maritalStatus}
                onChange={handleChange}
                disabled={dontHaveAccess}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {maritalStatusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("workStatus", lang) || "Work Status"}
              </label>
              <select
                name="workStatus"
                value={form.workStatus}
                onChange={handleChange}
                disabled={dontHaveAccess}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {workStatusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("education", lang) || "Education"}
              </label>
              <select
                name="education"
                value={form.education}
                onChange={handleChange}
                disabled={dontHaveAccess}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {educationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("occupation", lang) || "Occupation"}
              </label>
              <select
                name="occupation"
                value={form.occupation}
                disabled={dontHaveAccess}
                onChange={handleChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {occupationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <AccountSection>
            {t("exerciseAndDiet", lang) || "Exercise and Diet"}
          </AccountSection>
          <div className="mb-8">
            <div className="mb-6">
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("dietaryRoutine", lang) || "Dietary Routine"}
              </label>
              <select
                name="dietaryRoutine"
                value={form.dietaryRoutine}
                disabled={dontHaveAccess}
                onChange={handleChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {dietaryRoutineOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("exerciseFrequency", lang) || "Exercise Frequency"}
              </label>
              <select
                name="exerciseFrequency"
                value={form.exerciseFrequency}
                disabled={dontHaveAccess}
                onChange={handleChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {exerciseFrequencyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <AccountButton
              className="md:w-1/3"
              disabled={saving}
              onClick={() => goToTab("prev")}
            >
              <span className="mr-2">&laquo;</span> {t("back", lang) || "Back"}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => handleSave()}
              disabled={saving}
            >
              {saving ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("saving", lang) || "Saving..."}
                </div>
              ) : (
                t("save", lang) || "Save"
              )}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => goToTab("next")}
              disabled={saving}
            >
              <>
                {t("next", lang) || "Next"}
                <span className="ml-2">&raquo;</span>
              </>
            </AccountButton>
          </div>
        </div>
      </div>
    </div>
  );
}
